// SPDX-License-Identifier: MIT

// Consent enforcement — turns a set of granted/denied states into side effects:
// Google Consent Mode updates, a dataLayer event, and each type's onAccept /
// onReject callback (which is how site trackers load/unload). Pure logic aside
// from the window globals it drives, so it's unit-testable with a window stub.

const DEFAULT_EVENT_NAME = 'cookie_consent_update';

// Maps each type's `gtag` keys to granted/denied and sends one Consent Mode
// update, then pushes the dataLayer event so GTM tags keyed on it can trigger.
const updateGoogleConsent = (consentTypes, states, eventName) => {
  const update = {};
  consentTypes.forEach((type) => {
    if (!type.gtag) return;
    const keys = Array.isArray(type.gtag) ? type.gtag : [type.gtag];
    const value = states[type.id] ? 'granted' : 'denied';
    keys.forEach((key) => {
      update[key] = value;
    });
  });

  if (Object.keys(update).length > 0 && typeof window.gtag === 'function') {
    window.gtag('consent', 'update', update);
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName });
};

// Applies `states`. Google consent is always refreshed (cheap, idempotent).
//
// Per-type callbacks:
//   • On load (`options.onLoad`): only run onAccept for granted types, to load
//     stored-consent trackers. onReject is NEVER run on load — nothing is loaded
//     yet to unload, and a reject that reloads the page (e.g. to drop a pixel)
//     would loop on every load.
//   • On a change: run onAccept / onReject only for types whose state changed vs
//     `prevStates`, so trackers load/unload exactly once (and a revoke that
//     reloads fires once, for a real user action).
const applyConsent = (consentTypes, states, prevStates, options = {}) => {
  const eventName = options.eventName || DEFAULT_EVENT_NAME;

  updateGoogleConsent(consentTypes, states, eventName);

  consentTypes.forEach((type) => {
    const granted = !!states[type.id];

    if (options.onLoad) {
      if (granted && typeof type.onAccept === 'function') type.onAccept();
      return;
    }

    const previous = prevStates ? !!prevStates[type.id] : undefined;
    if (granted === previous) return;

    if (granted) {
      if (typeof type.onAccept === 'function') type.onAccept();
    } else if (typeof type.onReject === 'function') {
      type.onReject();
    }
  });
};

module.exports = { applyConsent, DEFAULT_EVENT_NAME };
